/**
 * Floating toolbar overlay — Strecke / Fläche / Löschen + colour swatches.
 *
 * Active tool is highlighted; clicking the same tool again deselects.
 * The colour row picks the hue for *new* measurements; already-placed
 * lines keep their original colour.
 */

export type ActiveTool = 'none' | 'distance' | 'area' | 'note'

interface ToolbarProps {
  active: ActiveTool
  onChange: (tool: ActiveTool) => void
  color: number
  onColorChange: (color: number) => void
  onPrint: () => void
  onScreenshot: () => void
  screenshotBusy?: boolean
  lineScale: number
  onLineScaleChange: (scale: number) => void
  labelScale: number
  onLabelScaleChange: (scale: number) => void
  measureScale: number
  onMeasureScaleChange: (scale: number) => void
  showAuthors: boolean
  onShowAuthorsChange: (show: boolean) => void
}

const baseButton: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: '#2a2a2a',
  color: '#eee',
  border: '1px solid #444',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
}

const activeButton: React.CSSProperties = {
  ...baseButton,
  background: '#ffd400',
  color: '#000',
  borderColor: '#ffd400',
}

// Hand-picked palette — distinct on aerial textures + colour-blind friendly
const COLOR_SWATCHES: { color: number; label: string }[] = [
  { color: 0xffd400, label: 'Gelb' },
  { color: 0xff3b30, label: 'Rot' },
  { color: 0x34c759, label: 'Grün' },
  { color: 0x4dd2ff, label: 'Blau' },
  { color: 0xff9500, label: 'Orange' },
  { color: 0xff2d95, label: 'Magenta' },
  { color: 0xffffff, label: 'Weiß' },
]

function hex(c: number): string {
  return '#' + c.toString(16).padStart(6, '0')
}

export function Toolbar({
  active,
  onChange,
  color,
  onColorChange,
  onPrint,
  onScreenshot,
  screenshotBusy = false,
  lineScale,
  onLineScaleChange,
  labelScale,
  onLabelScaleChange,
  measureScale,
  onMeasureScaleChange,
  showAuthors,
  onShowAuthorsChange,
}: ToolbarProps) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        }}
      >
        <button
          style={{
            ...(active === 'distance' ? activeButton : baseButton),
            borderRadius: '6px 0 0 6px',
          }}
          onClick={() => onChange(active === 'distance' ? 'none' : 'distance')}
        >
          Strecke
        </button>
        <button
          style={{
            ...(active === 'area' ? activeButton : baseButton),
            borderLeft: 'none',
          }}
          onClick={() => onChange(active === 'area' ? 'none' : 'area')}
        >
          Fläche
        </button>
        <button
          style={{
            ...(active === 'note' ? activeButton : baseButton),
            borderLeft: 'none',
          }}
          onClick={() => onChange(active === 'note' ? 'none' : 'note')}
          title="Notiz hinzufügen"
        >
          Notiz
        </button>
        <button
          style={{
            ...baseButton,
            borderLeft: 'none',
          }}
          onClick={onPrint}
          title="Drucken"
        >
          🖨
        </button>
        <button
          style={{
            ...baseButton,
            borderLeft: 'none',
            borderRadius: '0 6px 6px 0',
            opacity: screenshotBusy ? 0.6 : 1,
            cursor: screenshotBusy ? 'wait' : 'pointer',
          }}
          onClick={onScreenshot}
          disabled={screenshotBusy}
          title="Screenshot speichern + herunterladen"
        >
          📷
        </button>
      </div>

      {/* Toggle: bake author initials into the top-right corner of every
          measurement-value label (Linie/Fläche). Sits in the toolbar so it's
          one click away from the rest of the display options. */}
      <label
        title="Initialen des Autors in die obere rechte Ecke jedes Maß-Labels einblenden"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'rgba(20,20,20,0.85)',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          color: '#ddd',
          fontSize: '0.75rem',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <input
          type="checkbox"
          checked={showAuthors}
          onChange={(e) => onShowAuthorsChange(e.target.checked)}
          style={{ accentColor: '#ffd400', cursor: 'pointer' }}
        />
        Autor-Kürzel im Label
      </label>

      <div
        style={{
          display: 'flex',
          gap: '4px',
          padding: '6px 8px',
          background: 'rgba(20,20,20,0.85)',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '0.7rem', opacity: 0.6, marginRight: '4px' }}>Farbe</span>
        {/* see slider below */}
        {COLOR_SWATCHES.map((s) => {
          const isActive = s.color === color
          return (
            <button
              key={s.color}
              title={s.label}
              onClick={() => onColorChange(s.color)}
              aria-pressed={isActive}
              style={{
                width: '22px',
                height: '22px',
                borderRadius: '4px',
                background: hex(s.color),
                border: isActive
                  ? '2px solid #fff'
                  : '1px solid rgba(255,255,255,0.25)',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          )
        })}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'rgba(20,20,20,0.85)',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          color: '#ddd',
          fontSize: '0.7rem',
        }}
      >
        <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>Linienstärke</span>
        <input
          type="range"
          min={0.5}
          max={10}
          step={0.1}
          value={lineScale}
          onChange={(e) => onLineScaleChange(parseFloat(e.target.value))}
          style={{ flex: 1, accentColor: '#ffd400' }}
          aria-label="Linienstärke"
        />
        <span style={{ width: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {lineScale.toFixed(1)}×
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'rgba(20,20,20,0.85)',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          color: '#ddd',
          fontSize: '0.7rem',
        }}
      >
        <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>Textgröße</span>
        <input
          type="range"
          min={0.3}
          max={5}
          step={0.1}
          value={labelScale}
          onChange={(e) => onLabelScaleChange(parseFloat(e.target.value))}
          style={{ flex: 1, accentColor: '#ffd400' }}
          aria-label="Textgröße"
        />
        <span style={{ width: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {labelScale.toFixed(1)}×
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'rgba(20,20,20,0.85)',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          color: '#ddd',
          fontSize: '0.7rem',
        }}
      >
        <span style={{ opacity: 0.6, whiteSpace: 'nowrap' }}>Maßtexte</span>
        <input
          type="range"
          min={0.3}
          max={5}
          step={0.1}
          value={measureScale}
          onChange={(e) => onMeasureScaleChange(parseFloat(e.target.value))}
          style={{ flex: 1, accentColor: '#ffd400' }}
          aria-label="Maßtexte"
        />
        <span style={{ width: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {measureScale.toFixed(1)}×
        </span>
      </div>
    </div>
  )
}
